import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Camera,
  Users as UsersIcon, Search, KeyRound
} from 'lucide-react';

import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatAmount } from '../../utils/format';
import { compressImage } from '../../utils/imageCompressor';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', bg: '#F3E8FF', color: '#7C3AED' },
  admin:       { label: 'Administrador', bg: '#DBEAFE', color: '#2563EB' },
  waiter:      { label: 'Mesero', bg: '#D1FAE5', color: '#059669' },
  kitchen:     { label: 'Cocina', bg: '#FEF3C7', color: '#D97706' },
  cashier:     { label: 'Cajero', bg: '#FFF7ED', color: '#EA580C' },
  delivery:    { label: 'Delivery', bg: '#E0E7FF', color: '#4F46E5' },
  customer:    { label: 'Cliente', bg: '#F3F4F6', color: '#6B7280' }
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const AVATAR_COLORS = [
  '#00796B', '#2563EB', '#7C3AED', '#DC2626',
  '#D97706', '#059669', '#4F46E5', '#DB2777'
];
const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const UserManagementTab = () => {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const fileInputRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  // Ficha de fidelidad por teléfono: fuente de verdad del crédito (el POS lo
  // resuelve así al cobrar). Se usa para badge en la lista y datos del diálogo.
  const [loyaltyByPhone, setLoyaltyByPhone] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'waiter',
    tenant_id: '', phone: '', is_active: true,
    creditEnabled: false, creditLimit: 0, creditBalance: 0
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    loadUsers();
    loadLoyaltyCredit();
    // Solo el super_admin necesita el listado de restaurantes; un admin de
    // tenant gestiona unicamente los usuarios de su propio restaurante.
    if (isSuperAdmin) loadTenants();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al cargar usuarios'));
    }
  };

  const loadLoyaltyCredit = async () => {
    try {
      const { data } = await axios.get(`${API}/loyalty/customers`, { headers: getAuthHeaders() });
      const map = {};
      (Array.isArray(data) ? data : []).forEach((c) => {
        if (c.customer_phone) map[String(c.customer_phone).trim()] = c;
      });
      setLoyaltyByPhone(map);
    } catch (_err) {
      // Sin datos de fidelidad (tenant sin programa o sin permiso): el crédito
      // simplemente no se muestra; no es un error que deba interrumpir al admin.
    }
  };

  const loadTenants = async () => {
    try {
      const { data } = await axios.get(`${API}/tenants`, { headers: getAuthHeaders() });
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al cargar restaurantes'));
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Comprimir en el navegador (redimension + WebP): se acepta cualquier
    // imagen sin tope de tamano porque llega ya optimizada al backend.
    const compressedFile = await compressImage(file, { maxSize: 400 });
    setAvatarFile(compressedFile);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(compressedFile);
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    const fd = new FormData();
    fd.append('file', avatarFile);
    const { data } = await axios.post(`${API}/users/upload-avatar`, fd, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return data.avatar_url;
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      const lc = user.phone ? (loyaltyByPhone[String(user.phone).trim()] || {}) : {};
      setFormData({
        name: user.name, email: user.email, password: '',
        role: user.role, tenant_id: user.tenant_id || '',
        phone: user.phone || '', is_active: user.is_active !== false,
        creditEnabled: !!lc.credit_enabled,
        creditLimit: Number(lc.credit_limit || 0),
        creditBalance: Number(lc.credit_balance || 0)
      });
      setAvatarPreview(user.avatar_url ? getImageUrl(user.avatar_url) : null);
    } else {
      setEditingUser(null);
      setFormData({
        name: '', email: '', password: '', role: 'waiter',
        // Un admin de tenant crea usuarios dentro de su propio restaurante.
        tenant_id: isSuperAdmin ? '' : (currentUser?.tenant_id || ''),
        phone: '', is_active: true,
        creditEnabled: false, creditLimit: 0, creditBalance: 0
      });
      setAvatarPreview(null);
    }
    setAvatarFile(null);
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setTimeout(() => {
      setEditingUser(null);
      setAvatarFile(null);
      setAvatarPreview(null);
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }

    setLoading(true);
    try {
      let avatarUrl = editingUser?.avatar_url || null;

      if (avatarFile) {
        const uploaded = await uploadAvatar();
        if (uploaded) avatarUrl = uploaded;
      }

      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: formData.name, email: formData.email,
          role: formData.role, phone: formData.phone,
          avatar_url: avatarUrl
        }, { headers: getAuthHeaders() });
        // El crédito vive en la ficha de fidelidad del teléfono: solo se envía
        // si cambió, y después de guardar el usuario (por si el teléfono editado).
        const lc = editingUser.phone ? (loyaltyByPhone[String(editingUser.phone).trim()] || {}) : {};
        const creditChanged =
          !!lc.credit_enabled !== !!formData.creditEnabled ||
          Number(lc.credit_limit || 0) !== Number(formData.creditLimit || 0);
        if (creditChanged && (formData.phone || '').trim()) {
          await axios.put(`${API}/users/${editingUser.id}/credit`, {
            credit_enabled: formData.creditEnabled,
            credit_limit: Number(formData.creditLimit || 0)
          }, { headers: getAuthHeaders() });
        }
        toast.success('Usuario actualizado exitosamente');
      } else {
        await axios.post(`${API}/users`, {
          ...formData, avatar_url: avatarUrl
        }, { headers: getAuthHeaders() });
        toast.success('Usuario creado exitosamente');
      }

      handleCloseDialog();
      loadUsers();
      loadLoyaltyCredit();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar usuario'));
    }
    setLoading(false);
  };

  const handleToggleStatus = async (user) => {
    try {
      const { data } = await axios.patch(
        `${API}/users/${user.id}/toggle-status`, {},
        { headers: getAuthHeaders() }
      );
      toast.success(data.message || 'Estado actualizado');
      loadUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al cambiar estado'));
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${user.name}"?`)) return;
    try {
      await axios.delete(`${API}/users/${user.id}`, { headers: getAuthHeaders() });
      toast.success('Usuario eliminado exitosamente');
      loadUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al eliminar usuario'));
    }
  };

  const handleOpenPasswordDialog = (rowUser) => {
    setPasswordUser(rowUser);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setTimeout(() => {
      setPasswordUser(null);
      setNewPassword('');
    }, 100);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.patch(
        `${API}/users/${passwordUser.id}/password`,
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success('Contraseña actualizada exitosamente');
      handleClosePasswordDialog();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al cambiar la contraseña'));
    }
    setSavingPassword(false);
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderRoleBadge = (role) => {
    const cfg = ROLE_CONFIG[role] || { label: role, bg: '#F3F4F6', color: '#6B7280' };
    return (
      <span style={{
        background: cfg.bg, color: cfg.color,
        fontSize: 12, fontWeight: 600,
        padding: '4px 12px', borderRadius: 100
      }}>
        {cfg.label}
      </span>
    );
  };

  const renderAvatar = (user, size = 40) => {
    const hasPhoto = user.avatar_url;
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        overflow: 'hidden', flexShrink: 0,
        background: hasPhoto ? 'transparent' : getAvatarColor(user.name),
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {hasPhoto ? (
          <img
            src={getImageUrl(user.avatar_url)}
            alt={user.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.textContent = getInitials(user.name); }}
          />
        ) : (
          <span style={{ color: '#fff', fontSize: size * 0.38, fontWeight: 600, letterSpacing: 0.5 }}>
            {getInitials(user.name)}
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: '#F8F9FA', minHeight: '100%', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 24, flexWrap: 'wrap', gap: 16
      }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#11181C', margin: 0 }}>
            Gestión de Usuarios
          </h2>
          <p style={{ fontSize: 14, color: '#567482', margin: 0, marginTop: 4 }}>
            {isSuperAdmin
              ? 'Administra todos los usuarios del sistema'
              : 'Administra los usuarios de tu restaurante'}
          </p>
        </div>
        {hasPermission('users:create') && (
          <Button
            onClick={() => handleOpenDialog()}
            style={{
              background: '#00796B', color: '#fff', border: 'none',
              borderRadius: 12, padding: '10px 20px', fontWeight: 600,
              fontSize: 14, cursor: 'pointer', display: 'flex',
              alignItems: 'center', gap: 8
            }}
          >
            <Plus style={{ width: 16, height: 16 }} />
            Nuevo Usuario
          </Button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16, position: 'relative', maxWidth: 360 }}>
        <Search style={{
          position: 'absolute', left: 12, top: '50%',
          transform: 'translateY(-50%)', width: 16, height: 16, color: '#567482'
        }} />
        <input
          type="text"
          placeholder="Buscar por nombre, email o teléfono..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%', padding: '10px 12px 10px 38px',
            border: '1px solid #E2E8F0', borderRadius: 12,
            fontSize: 14, color: '#11181C', background: '#fff',
            outline: 'none', boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Table */}
      <Card style={{
        background: '#fff', borderRadius: 16,
        border: '1px solid #E2E8F0', overflow: 'hidden', padding: 0
      }}>
        {/* Table header row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1.5fr 1fr 1fr 80px 80px 120px',
          padding: '12px 24px', borderBottom: '1px solid #E2E8F0',
          fontSize: 12, fontWeight: 600, color: '#567482',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          alignItems: 'center'
        }}>
          <div>Usuario</div>
          <div>Email</div>
          <div>Teléfono</div>
          <div>Rol</div>
          <div style={{ textAlign: 'center' }}>Sesiones</div>
          <div style={{ textAlign: 'center' }}>Estado</div>
          <div style={{ textAlign: 'center' }}>Acciones</div>
        </div>

        {/* Table body */}
        {filteredUsers.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center' }}>
            <UsersIcon style={{ width: 48, height: 48, color: '#CBD5E1', marginBottom: 12 }} />
            <p style={{ color: '#567482', fontSize: 14, margin: 0 }}>
              {searchQuery ? 'No se encontraron resultados' : 'No hay usuarios creados'}
            </p>
          </div>
        ) : (
          filteredUsers.map((user, idx) => (
            <div
              key={user.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1fr 1fr 80px 80px 120px',
                padding: '16px 24px',
                borderBottom: idx < filteredUsers.length - 1 ? '1px solid #E2E8F0' : 'none',
                alignItems: 'center', transition: 'background 0.15s',
                cursor: 'default'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Usuario */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {renderAvatar(user, 40)}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#11181C' }}>
                    {user.name}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {renderRoleBadge(user.role)}
                    {user.phone && loyaltyByPhone[String(user.phone).trim()]?.credit_enabled && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: '#059669',
                        border: '1px solid #34D399', padding: '2px 8px', borderRadius: 100
                      }}>
                        Crédito
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Email */}
              <div style={{ fontSize: 14, color: '#567482' }}>{user.email}</div>

              {/* Teléfono */}
              <div style={{ fontSize: 14, color: '#567482' }}>{user.phone || '—'}</div>

              {/* Rol */}
              <div>
                {renderRoleBadge(user.role)}
              </div>

              {/* Sesiones */}
              <div style={{ textAlign: 'center', fontSize: 14, color: '#567482' }}>0</div>

              {/* Estado toggle */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {hasPermission('users:edit') ? (
                  <Switch
                    checked={user.is_active !== false}
                    onCheckedChange={() => handleToggleStatus(user)}
                  />
                ) : (
                  <span style={{
                    fontSize: 12, fontWeight: 600,
                    color: user.is_active !== false ? '#059669' : '#DC2626'
                  }}>
                    {user.is_active !== false ? 'Activo' : 'Inactivo'}
                  </span>
                )}
              </div>

              {/* Acciones */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                {hasPermission('users:edit') && (
                  <button
                    onClick={() => handleOpenDialog(user)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 6, borderRadius: 8, color: '#567482',
                      transition: 'color 0.15s', display: 'flex'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#00796B'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#567482'; }}
                    title="Editar usuario"
                  >
                    <Pencil style={{ width: 16, height: 16 }} />
                  </button>
                )}
                {hasPermission('users:edit') && (
                  <button
                    onClick={() => handleOpenPasswordDialog(user)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 6, borderRadius: 8, color: '#567482',
                      transition: 'color 0.15s', display: 'flex'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#D97706'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#567482'; }}
                    title="Cambiar contraseña"
                  >
                    <KeyRound style={{ width: 16, height: 16 }} />
                  </button>
                )}
                {hasPermission('users:delete') && (
                  <button
                    onClick={() => handleDelete(user)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 6, borderRadius: 8, color: '#567482',
                      transition: 'color 0.15s', display: 'flex'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#E53E3E'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#567482'; }}
                    title="Eliminar usuario"
                  >
                    <Trash2 style={{ width: 16, height: 16 }} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </Card>

      {/* Modal */}
      <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent style={{ maxWidth: 600, borderRadius: 16, fontFamily: "'Inter', sans-serif" }}>
          <DialogHeader>
            <DialogTitle style={{ fontSize: 20, fontWeight: 700, color: '#11181C' }}>
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
            <DialogDescription style={{ color: '#567482', fontSize: 14 }}>
              {editingUser
                ? 'Modifica los datos del usuario seleccionado'
                : 'Crea un nuevo usuario y asígnale un rol y restaurante'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
              {/* Left: Avatar */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 120 }}>
                <div
                  style={{
                    width: 120, height: 120, borderRadius: '50%',
                    overflow: 'hidden', position: 'relative', cursor: 'pointer',
                    background: avatarPreview ? 'transparent' : '#E2E8F0',
                    border: '2px dashed #CBD5E1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : editingUser?.avatar_url ? (
                    <img
                      src={getImageUrl(editingUser.avatar_url)}
                      alt={editingUser.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Camera style={{ width: 24, height: 24, color: '#94A3B8' }} />
                  )}
                  {/* Hover overlay */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.2s',
                    borderRadius: '50%'
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = 0; }}
                  >
                    <Camera style={{ width: 24, height: 24, color: '#fff' }} />
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <span style={{ fontSize: 12, color: '#567482', textAlign: 'center' }}>
                  {avatarPreview ? 'Cambiar foto' : 'Subir foto'}
                </span>
              </div>

              {/* Right: Form */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                    Nombre completo *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Juan Pérez"
                    required
                    style={{ borderRadius: 12, fontSize: 14 }}
                  />
                </div>

                <div>
                  <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                    Email *
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="usuario@ejemplo.com"
                    required
                    style={{ borderRadius: 12, fontSize: 14 }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                      Teléfono
                    </Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1234567890"
                      style={{ borderRadius: 12, fontSize: 14 }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                      Rol *
                    </Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger style={{ borderRadius: 12, fontSize: 14 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="waiter">Mesero</SelectItem>
                        <SelectItem value="kitchen">Cocina</SelectItem>
                        <SelectItem value="cashier">Cajero</SelectItem>
                        <SelectItem value="delivery">Delivery</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {!editingUser && (
                  <div>
                    <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                      Contraseña *
                    </Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      style={{ borderRadius: 12, fontSize: 14 }}
                    />
                  </div>
                )}

                {isSuperAdmin && (
                  <div>
                    <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                      Restaurante
                    </Label>
                    <Select
                      value={formData.tenant_id}
                      onValueChange={(value) => setFormData({ ...formData, tenant_id: value })}
                    >
                      <SelectTrigger style={{ borderRadius: 12, fontSize: 14 }}>
                        <SelectValue placeholder="Selecciona restaurante" />
                      </SelectTrigger>
                      <SelectContent>
                        {tenants.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Estado toggle */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', background: '#F8FAFC', borderRadius: 12,
                  border: '1px solid #E2E8F0'
                }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Estado</span>
                    <p style={{ fontSize: 12, color: '#567482', margin: 0 }}>
                      {formData.is_active ? 'Usuario activo' : 'Usuario inactivo'}
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>

                {/* Crédito: solo en edición y requiere teléfono (la ficha de
                    fidelidad se clavea por el teléfono del usuario) */}
                {editingUser && (
                  <div style={{
                    padding: '10px 14px', background: '#F0FDF4', borderRadius: 12,
                    border: '1px solid #BBF7D0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Crédito</span>
                        <p style={{ fontSize: 12, color: '#567482', margin: 0 }}>
                          {(formData.phone || '').trim()
                            ? 'Permite cobrar órdenes de este cliente a crédito en el POS'
                            : 'Requiere asignarle un teléfono al usuario'}
                        </p>
                      </div>
                      <Switch
                        checked={formData.creditEnabled}
                        disabled={!(formData.phone || '').trim()}
                        onCheckedChange={(checked) => setFormData({ ...formData, creditEnabled: checked })}
                      />
                    </div>
                    {formData.creditEnabled && (
                      <div style={{ marginTop: 10 }}>
                        <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                          Cupo de crédito (0 = sin límite)
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={formData.creditLimit}
                          onChange={(e) => setFormData({ ...formData, creditLimit: e.target.value })}
                          placeholder="Ej: 5000"
                          style={{ borderRadius: 12, fontSize: 14 }}
                        />
                        {Number(formData.creditBalance || 0) > 0 && (
                          <p style={{ fontSize: 12, color: '#DC2626', margin: '6px 0 0' }}>
                            Saldo pendiente actual: ${formatAmount(formData.creditBalance)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer buttons */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 12,
              marginTop: 24, paddingTop: 16, borderTop: '1px solid #E2E8F0'
            }}>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                style={{
                  borderRadius: 12, padding: '10px 24px',
                  fontSize: 14, fontWeight: 600, color: '#374151'
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                style={{
                  background: '#00796B', color: '#fff', border: 'none',
                  borderRadius: 12, padding: '10px 24px',
                  fontSize: 14, fontWeight: 600
                }}
              >
                {loading ? 'Guardando...' : 'Guardar Usuario'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal cambiar contraseña */}
      <Dialog open={showPasswordDialog} onOpenChange={handleClosePasswordDialog}>
        <DialogContent style={{ maxWidth: 420, borderRadius: 16, fontFamily: "'Inter', sans-serif" }}>
          <DialogHeader>
            <DialogTitle style={{ fontSize: 20, fontWeight: 700, color: '#11181C' }}>
              Cambiar contraseña
            </DialogTitle>
            <DialogDescription style={{ color: '#567482', fontSize: 14 }}>
              Establece una nueva contraseña para {passwordUser?.name}. No necesitas
              conocer la anterior.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleChangePassword}>
            <div style={{ marginTop: 8 }}>
              <Label style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>
                Nueva contraseña *
              </Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                autoFocus
                style={{ borderRadius: 12, fontSize: 14 }}
              />
            </div>

            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 12,
              marginTop: 24, paddingTop: 16, borderTop: '1px solid #E2E8F0'
            }}>
              <Button
                type="button"
                variant="outline"
                onClick={handleClosePasswordDialog}
                style={{
                  borderRadius: 12, padding: '10px 24px',
                  fontSize: 14, fontWeight: 600, color: '#374151'
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingPassword}
                style={{
                  background: '#00796B', color: '#fff', border: 'none',
                  borderRadius: 12, padding: '10px 24px',
                  fontSize: 14, fontWeight: 600
                }}
              >
                {savingPassword ? 'Guardando...' : 'Actualizar contraseña'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementTab;
